// <fiee-map> makes the kiosk floor plan interactive (view/panel_servicios.templ).
//
//   - Pinch, drag and mouse-wheel zoom/pan, plus +/−/reset buttons. The plan is
//     a single transformed stage; pins live inside it and are counter-scaled in
//     CSS (--zoom) so they stay finger-sized at any zoom.
//   - Tapping a pin opens a small box with the place's name next to it.
//   - Any [data-map-focus="N"] in the same floor block (the directory under the
//     map) flies the plan to pin N and opens its box.
//
// The floor selector is plain HTMX: it replaces the whole floor block, so every
// floor starts with a fresh <fiee-map> and no state carries over.
export class FieeMap extends HTMLElement {
  static readonly MIN = 1;
  static readonly MAX = 4;
  static readonly TAP_SLOP = 8; // px a finger may wander and still be a tap

  #viewport!: HTMLElement;
  #stage!: HTMLElement;
  #tip!: HTMLElement;

  #scale = 1;
  #x = 0;
  #y = 0;

  #pointers = new Map<number, { x: number; y: number }>();
  #gesture: { scale: number; x: number; y: number; dist: number; cx: number; cy: number } | null = null;
  #dragged = false;
  #scope: Element | Document = document;
  #onFocusClick = (e: Event) => this.#focusClick(e);

  connectedCallback() {
    const viewport = this.querySelector<HTMLElement>("[data-map-viewport]");
    const stage = this.querySelector<HTMLElement>("[data-map-stage]");
    const tip = this.querySelector<HTMLElement>("[data-map-tip]");
    if (!viewport || !stage || !tip) return;
    this.#viewport = viewport;
    this.#stage = stage;
    this.#tip = tip;

    viewport.addEventListener("pointerdown", (e) => this.#down(e));
    viewport.addEventListener("pointermove", (e) => this.#move(e));
    viewport.addEventListener("pointerup", (e) => this.#up(e));
    viewport.addEventListener("pointercancel", (e) => this.#up(e));
    viewport.addEventListener("wheel", (e) => this.#wheel(e), { passive: false });

    stage.addEventListener("click", (e) => {
      if (this.#dragged) return; // the end of a drag is not a tap
      const pin = (e.target as Element).closest<HTMLElement>("[data-pin]");
      if (pin) this.#showTip(pin);
    });
    tip.querySelector("[data-tip-close]")?.addEventListener("click", () => this.#hideTip());

    this.querySelectorAll<HTMLButtonElement>("[data-zoom]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const { width, height } = viewport.getBoundingClientRect();
        switch (btn.dataset["zoom"]) {
          case "in":
            this.#zoomAt(width / 2, height / 2, this.#scale * 1.5, true);
            break;
          case "out":
            this.#zoomAt(width / 2, height / 2, this.#scale / 1.5, true);
            break;
          default:
            this.#reset();
        }
      });
    });

    // The directory is outside this element but inside the same floor block.
    this.#scope = this.closest("#panel-servicios") ?? document;
    this.#scope.addEventListener("click", this.#onFocusClick);

    this.#apply(false);
  }

  disconnectedCallback() {
    this.#scope.removeEventListener("click", this.#onFocusClick);
  }

  // --- gestures ---------------------------------------------------------

  #down(e: PointerEvent) {
    this.#pointers.set(e.pointerId, this.#local(e));
    this.#dragged = false;
    this.#startGesture();
  }

  #move(e: PointerEvent) {
    if (!this.#pointers.has(e.pointerId) || !this.#gesture) return;
    this.#pointers.set(e.pointerId, this.#local(e));
    const g = this.#gesture;
    const { cx, cy, dist } = this.#centroid();

    if (!this.#dragged) {
      if (Math.hypot(cx - g.cx, cy - g.cy) < FieeMap.TAP_SLOP && Math.abs(dist - g.dist) < FieeMap.TAP_SLOP) {
        return;
      }
      // Only now take the pointer: capturing on pointerdown would retarget
      // the click of a simple tap away from the pin.
      this.#dragged = true;
      this.#viewport.setPointerCapture(e.pointerId);
    }

    const scale = this.#pointers.size > 1 && g.dist > 0 ? this.#clampScale(g.scale * (dist / g.dist)) : g.scale;
    // Keep the point under the fingers where it was when the gesture began.
    const k = scale / g.scale;
    this.#scale = scale;
    this.#x = cx - (g.cx - g.x) * k;
    this.#y = cy - (g.cy - g.y) * k;
    this.#apply(false);
  }

  #up(e: PointerEvent) {
    this.#pointers.delete(e.pointerId);
    // Lifting one finger of a pinch continues as a drag from where it is.
    this.#startGesture();
    if (this.#pointers.size === 0) this.#gesture = null;
  }

  #wheel(e: WheelEvent) {
    e.preventDefault();
    const { x, y } = this.#local(e);
    this.#zoomAt(x, y, this.#scale * Math.exp(-e.deltaY * 0.0015), false);
  }

  #startGesture() {
    if (this.#pointers.size === 0) return;
    const { cx, cy, dist } = this.#centroid();
    this.#gesture = { scale: this.#scale, x: this.#x, y: this.#y, dist, cx, cy };
  }

  #centroid() {
    const pts = [...this.#pointers.values()];
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    const [a, b] = pts;
    const dist = a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    return { cx, cy, dist };
  }

  #local(e: MouseEvent) {
    const r = this.#viewport.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  // --- transform --------------------------------------------------------

  #zoomAt(px: number, py: number, scale: number, animate: boolean) {
    scale = this.#clampScale(scale);
    const k = scale / this.#scale;
    this.#x = px - (px - this.#x) * k;
    this.#y = py - (py - this.#y) * k;
    this.#scale = scale;
    this.#apply(animate);
  }

  #reset() {
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;
    this.#hideTip();
    this.#apply(true);
  }

  #clampScale(s: number) {
    return Math.min(FieeMap.MAX, Math.max(FieeMap.MIN, s));
  }

  #apply(animate: boolean) {
    // The stage is exactly the viewport's size at scale 1, so keeping it
    // covering the viewport is a clamp of the translation.
    const w = this.#viewport.clientWidth;
    const h = this.#viewport.clientHeight;
    this.#x = Math.min(0, Math.max(w - w * this.#scale, this.#x));
    this.#y = Math.min(0, Math.max(h - h * this.#scale, this.#y));

    this.#stage.classList.toggle("is-animating", animate);
    this.#stage.style.transform = `translate(${this.#x}px, ${this.#y}px) scale(${this.#scale})`;
    this.style.setProperty("--zoom", String(this.#scale));
    // Zoomed in, a one-finger drag pans the plan; at full view it scrolls the
    // page, or the plan would trap the visitor's swipe.
    this.toggleAttribute("data-zoomed", this.#scale > 1.001);
  }

  // --- pins -------------------------------------------------------------

  #showTip(pin: HTMLElement) {
    this.querySelectorAll(".panel-pin.is-selected").forEach((p) => p.classList.remove("is-selected"));
    pin.classList.add("is-selected");

    const set = (sel: string, text: string) => {
      const el = this.#tip.querySelector<HTMLElement>(sel);
      if (el) {
        el.textContent = text;
        el.hidden = text === "";
      }
    };
    set("[data-tip-num]", pin.dataset["pin"] ?? "");
    set("[data-tip-title]", pin.dataset["title"] ?? "");
    set("[data-tip-desc]", pin.dataset["desc"] ?? "");

    this.#tip.style.left = pin.style.left;
    this.#tip.style.top = pin.style.top;
    // Near an edge the box would leave the plan: open it toward the centre.
    const left = parseFloat(pin.style.left);
    this.#tip.classList.toggle("is-below", parseFloat(pin.style.top) < 18);
    this.#tip.classList.toggle("is-start", left < 30);
    this.#tip.classList.toggle("is-end", left > 70);
    this.#tip.className = this.#tip.className.replace(/\bis-(blue|green|purple|orange|red|teal|gold)\b/g, "").trim();
    const accent = [...pin.classList].find((c) => /^is-(blue|green|purple|orange|red|teal|gold)$/.test(c));
    if (accent) this.#tip.classList.add(accent);
    this.#tip.hidden = false;
  }

  #hideTip() {
    this.#tip.hidden = true;
    this.querySelectorAll(".panel-pin.is-selected").forEach((p) => p.classList.remove("is-selected"));
  }

  #focusClick(e: Event) {
    const trigger = (e.target as Element).closest<HTMLElement>("[data-map-focus]");
    if (!trigger) return;
    const pin = this.querySelector<HTMLElement>(`[data-pin="${CSS.escape(trigger.dataset["mapFocus"] ?? "")}"]`);
    if (!pin) return;

    this.scrollIntoView({ behavior: "smooth", block: "center" });
    // Pin coordinates are percentages of the stage, which at scale 1 is the
    // viewport: centre that point at 2.2×.
    const w = this.#viewport.clientWidth;
    const h = this.#viewport.clientHeight;
    const scale = 2.2;
    this.#scale = scale;
    this.#x = w / 2 - (parseFloat(pin.style.left) / 100) * w * scale;
    this.#y = h / 2 - (parseFloat(pin.style.top) / 100) * h * scale;
    this.#apply(true);
    this.#showTip(pin);
  }
}
